import { CLASSES_PER_PAGE } from "../constants/class.constants";
import {
  createClassFromForm,
  MOCK_LINKABLE_GRADES,
  SEED_ADMIN_CLASSES,
  buildLevelsFromForm,
  categorySlugFromLevels,
} from "../data/class.mock";
import { formValuesToPayload, slugifyClassName } from "../utils/class.utils";
import {
  loadMigratedClassStore,
  saveClassStore,
} from "../utils/classStorage";
import type { ClassApi } from "./classApi.interface";
import type { ClassFormSchemaValues } from "../schemas/classForm.schema";
import type {
  AdminClass,
  ClassExportFormat,
  ClassExportResult,
  ClassListQuery,
  ClassListResult,
  ClassMutationResult,
} from "../types/class.types";

const delay = (ms = 400) => new Promise((r) => setTimeout(r, ms));

function loadStore(): AdminClass[] {
  return loadMigratedClassStore(SEED_ADMIN_CLASSES);
}

function saveStore(classes: AdminClass[]) {
  saveClassStore(classes);
}

let memoryStore: AdminClass[] | null = null;

function getStore(): AdminClass[] {
  if (!memoryStore) {
    memoryStore = loadStore();
  }
  return memoryStore;
}

function persist(classes: AdminClass[]) {
  memoryStore = classes;
  saveStore(classes);
}

function filterByDate(
  classes: AdminClass[],
  fromDate?: Date,
  toDate?: Date
): AdminClass[] {
  if (!fromDate && !toDate) return classes;
  return classes.filter((c) => {
    const created = new Date(c.createdAt).getTime();
    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      if (created < start.getTime()) return false;
    }
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      if (created > end.getTime()) return false;
    }
    return true;
  });
}

export const classApiMock: ClassApi = {
  async listClasses(query: ClassListQuery): Promise<ClassListResult> {
    await delay();
    const page = query.page ?? 1;
    const pageSize = query.pageSize ?? CLASSES_PER_PAGE;
    const search = (query.search ?? "").trim().toLowerCase();

    let items = [...getStore()];
    items = filterByDate(items, query.fromDate, query.toDate);

    if (search) {
      items = items.filter(
        (c) =>
          c.name.toLowerCase().includes(search) ||
          c.description.toLowerCase().includes(search)
      );
    }

    if (query.sortBy) {
      const dir = query.sortOrder === "desc" ? -1 : 1;
      const key = query.sortBy;
      items = [...items].sort((a, b) => {
        const av = (a as unknown as Record<string, unknown>)[key];
        const bv = (b as unknown as Record<string, unknown>)[key];
        if (av == null && bv == null) return 0;
        if (av == null) return -dir;
        if (bv == null) return dir;
        if (typeof av === "number" && typeof bv === "number") {
          return (av - bv) * dir;
        }
        return String(av).localeCompare(String(bv)) * dir;
      });
    }

    const totalItems = items.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const currentPage = Math.min(page, totalPages);
    const start = (currentPage - 1) * pageSize;

    return {
      data: items.slice(start, start + pageSize),
      totalItems,
      totalPages,
      currentPage,
    };
  },

  async getClassById(id: string) {
    await delay(200);
    return getStore().find((c) => c.id === id) ?? null;
  },

  async createClass(values: ClassFormSchemaValues): Promise<ClassMutationResult> {
    await delay(600);
    const payload = formValuesToPayload(values);
    const created = await createClassFromForm(payload);
    const store = getStore();
    persist([created, ...store]);
    return { class: created, message: "" };
  },

  async updateClass(
    id: string,
    values: ClassFormSchemaValues
  ): Promise<ClassMutationResult> {
    await delay(600);
    const payload = formValuesToPayload(values);
    const store = getStore();
    const idx = store.findIndex((c) => c.id === id);
    if (idx < 0) throw new Error("Class not found");

    const existing = store[idx];
    const levels = await buildLevelsFromForm(
      payload.level1Id,
      payload.level2Id,
      payload.level3Id
    );
    const updated: AdminClass = {
      ...existing,
      name: payload.name,
      slug: slugifyClassName(payload.name),
      categorySlug: await categorySlugFromLevels(payload.level1Id, payload.level2Id),
      description: payload.description,
      validityDays: payload.validityDays,
      price: payload.price,
      taxPercent: payload.taxPercent,
      totalPrice: payload.totalPrice,
      levels,
      benefits: payload.benefits,
      school: payload.school,
      thumbnailUrl: payload.thumbnailUrl,
      uploadId: payload.uploadId || existing.uploadId,
      schoolId: payload.schoolId || existing.schoolId,
      courseId: payload.courseId || existing.courseId,
      updatedAt: new Date().toISOString(),
    };

    const next = [...store];
    next[idx] = updated;
    persist(next);
    return { class: updated, message: "" };
  },

  async toggleClassActive(
    id: string,
    isActive: boolean
  ): Promise<ClassMutationResult> {
    await delay(300);
    const store = getStore();
    const idx = store.findIndex((c) => c.id === id);
    if (idx < 0) throw new Error("Class not found");
    const next = [...store];
    next[idx] = {
      ...next[idx],
      isActive,
      updatedAt: new Date().toISOString(),
    };
    persist(next);
    return { class: next[idx], message: "" };
  },

  async exportClasses(
    format: ClassExportFormat,
    query: Pick<ClassListQuery, "search" | "fromDate" | "toDate">
  ): Promise<ClassExportResult> {
    await delay(500);
    const { data } = await this.listClasses({
      ...query,
      page: 1,
      pageSize: 10_000,
    });

    const header =
      "S.No,Class Name,Students Onboarded,Total Price,Validity Days,Active\n";
    const rows = data
      .map(
        (c, i) =>
          `${i + 1},"${c.name.replace(/"/g, '""')}",${c.studentsOnboarded},${c.totalPrice},${c.validityDays},${c.isActive}`
      )
      .join("\n");

    const content = header + rows;
    const type =
      format === "pdf"
        ? "application/pdf"
        : format === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    return { data: new Blob([content], { type }), message: "" };
  },

  async listLinkableGrades() {
    await delay(100);
    return MOCK_LINKABLE_GRADES;
  },
};
